import React, { useState } from 'react';
import './Gallery.css';
import { useTranslation } from '../../i18n/TranslationContext';

interface GalleryItem {
  id: number;
  src: string;
  alt: string;
  category: string;
  title: string;
  description: string;
}

const categories = [
  { key: 'all', icon: 'fas fa-th' },
  { key: 'excavation', icon: 'fas fa-truck-monster' },
  { key: 'landscaping', icon: 'fas fa-tree' },
  { key: 'preparation', icon: 'fas fa-mountain' },
  { key: 'concrete', icon: 'fas fa-th-large' }
];

const Gallery: React.FC = () => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  
  // Gallery images using translations
  const galleryItems: GalleryItem[] = [
    { 
      id: 1, 
      src: '/img/services/excaresi.jpg', 
      alt: t.gallery.items.residentialExcavation.title, 
      category: 'excavation',
      title: t.gallery.items.residentialExcavation.title,
      description: t.gallery.items.residentialExcavation.description
    },
    { 
      id: 2, 
      src: '/img/excavator_scene.webp', 
      alt: t.gallery.items.poolExcavation.title, 
      category: 'excavation',
      title: t.gallery.items.poolExcavation.title,
      description: t.gallery.items.poolExcavation.description
    },
    { 
      id: 3, 
      src: '/img/services/prepsite.png', 
      alt: t.gallery.items.sitePreperation.title, 
      category: 'preparation',
      title: t.gallery.items.sitePreperation.title,
      description: t.gallery.items.sitePreperation.description
    },
    { 
      id: 4, 
      src: '/img/services/amenpays.jpeg', 
      alt: t.gallery.items.landscaping.title, 
      category: 'landscaping',
      title: t.gallery.items.landscaping.title,
      description: t.gallery.items.landscaping.description
    },
    { 
      id: 5, 
      src: '/img/services/beton.jpg', 
      alt: t.gallery.items.concrete.title, 
      category: 'concrete',
      title: t.gallery.items.concrete.title,
      description: t.gallery.items.concrete.description
    },
    { 
      id: 6, 
      src: '/img/1000023679.webp', 
      alt: t.gallery.items.memorialSpace.title, 
      category: 'excavation',
      title: t.gallery.items.memorialSpace.title,
      description: t.gallery.items.memorialSpace.description
    }
  ];

  const openModal = (id: number) => {
    setSelectedImage(id);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const filterGalleryItems = () => {
    if (activeCategory === 'all') {
      return galleryItems;
    }
    return galleryItems.filter(item => item.category === activeCategory);
  };

  const filteredItems = filterGalleryItems();
  const selectedItem = selectedImage ? galleryItems.find(img => img.id === selectedImage) : null;

  return (
    <section id="gallery" className="gallery-section">
      <div className="gallery-glow"></div>
      <div className="container">
        <h2 className="section-title">{t.gallery.title}</h2>
        
        <div className="gallery-categories">
          {categories.map((category) => (
            <div 
              key={category.key} 
              className={`category-tab ${activeCategory === category.key ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.key)}
            >
              <div className="category-icon">
                <i className={category.icon}></i>
              </div>
              <span>
                {category.key === 'all' && t.gallery.categories.all}
                {category.key === 'excavation' && t.gallery.categories.excavation}
                {category.key === 'landscaping' && t.gallery.categories.landscaping}
                {category.key === 'preparation' && t.gallery.categories.preparation}
                {category.key === 'concrete' && t.gallery.categories.concrete}
              </span>
              {activeCategory === category.key && (
                <div className="category-indicator"></div>
              )}
            </div>
          ))}
        </div>
        
        <div className="gallery-grid">
          {filteredItems.length > 0 ? (
            filteredItems.map((image) => (
              <div 
                key={image.id} 
                className="gallery-item"
                onClick={() => openModal(image.id)}
              >
                <img src={image.src} alt={image.alt} loading="lazy" />
                <div className="gallery-overlay">
                  <div className="overlay-content">
                    <h3>{image.title}</h3>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-items-message">
              <i className="fas fa-exclamation-circle"></i>
              <p>{t.gallery.noItems}</p>
            </div>
          )}
        </div>
      </div>

      {selectedImage !== null && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content glass-panel" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={closeModal}>
              <i className="fas fa-times"></i>
            </button>
            <div className="modal-image-container">
              <img src={selectedItem.src} alt={selectedItem.alt} />
            </div>
            <div className="modal-details">
              <h3>{selectedItem.title}</h3>
              <p>{selectedItem.description}</p>
              <div className="modal-category">
                <span className="category-tag">
                  {selectedItem.category === 'excavation' && t.gallery.categories.excavation}
                  {selectedItem.category === 'landscaping' && t.gallery.categories.landscaping}
                  {selectedItem.category === 'preparation' && t.gallery.categories.preparation}
                  {selectedItem.category === 'concrete' && t.gallery.categories.concrete}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;